import type { ProjectWithJobType } from "@repo/app-types/entities/project";
import {
  buildPersonName,
  buildTrainingContactDisplayNames,
} from "@repo/app-types/helpers/utilities";
import { DefaultSkeleton } from "@repo/ui/Skeletons/skeletons";
import {
  SlideoverDialog,
  SlideoverDialogContent,
  SlideoverDialogHeader,
} from "@repo/ui/Slideover/slideoverDialog";
import { SmallTimeStamp } from "@repo/ui/SmallTimeStamp/smallTimeStamp";
import { type FC, useId } from "react";
import { Heading } from "react-aria-components";
import { useParams } from "react-router";
import {
  useDeleteTrainingParticipantMutation,
  useTrainingParticipantQuery,
} from "../../api/entities/trainingParticipant.js";
import { AudioPlayer } from "../../components/audioPlayer/audioPlayer.js";
import { AudioPlayerProvider } from "../../components/audioPlayer/provider.js";
import { useSectionsForJobType } from "../projects/projectSettings/training/courseHooks.js";
import type { TreeItemKey } from "../projects/projectSettings/training/courseTreeHelpers.js";
import { useCourseTreeSelection } from "../projects/projectSettings/training/courseTreeInteractions.js";
import { ChapterDetail } from "./chapterDetail.js";
import { LessonDetail } from "./lessonDetail.js";
import { ParticipantCourseTree } from "./participantCourseTree.js";
import { TrainingParticipantMoreMenuButton } from "./trainingParticipantMoreMenuButton.js";

export interface TrainingParticipantDialogProps {
  project: ProjectWithJobType;
  initialSelectedKey?: TreeItemKey;
}

export const TrainingParticipantDialog: FC<TrainingParticipantDialogProps> = (
  props,
) => {
  const { participantId } = useParams();
  if (!participantId) {
    throw new Error("Expected participantId param");
  }

  const {
    sectionsById,
    isLoading: isLoadingSections,
    isError: isErrorSections,
  } = useSectionsForJobType(props.project.job_type.id);

  const {
    data: participant,
    isLoading: isLoadingParticipant,
    isError: isErrorParticipant,
  } = useTrainingParticipantQuery({
    projectId: props.project.id,
    participantId,
  });

  const [deleteParticipant] = useDeleteTrainingParticipantMutation();

  const {
    // For the `Tree` component
    selectedKeys,
    onSelectionChange,
    // For us
    selectedChapterNumber,
    selectedChapter,
    selectedLessonNumber,
    selectedSection,
  } = useCourseTreeSelection({
    initialSelectedKey: props.initialSelectedKey,
    course: participant?.course,
    sectionsById,
  });

  const onDeleteConfirm = async ({ close }: { close: () => void }) => {
    close();
    await deleteParticipant({
      projectId: props.project.id,
      participantId,
    });
  };

  const renderHeadingLeft = () => {
    if (!participant) {
      return null;
    }

    const { primaryDisplayName, secondaryDisplayName } =
      buildTrainingContactDisplayNames(participant.contact);

    return (
      <div className="px-2">
        <Heading slot="title" className="flex items-baseline gap-1">
          <span className="truncate text-base font-semibold text-foreground">
            {primaryDisplayName}
          </span>

          <SmallTimeStamp timestamp={participant.last_activity_at} />
        </Heading>

        {secondaryDisplayName ? (
          <div className="truncate text-xs text-gray-600">
            {secondaryDisplayName}
          </div>
        ) : null}
      </div>
    );
  };

  const treeLabelId = useId();

  return (
    <SlideoverDialog label="Training participant">
      {({ close }) => (
        <>
          <SlideoverDialogHeader
            isLoading={isLoadingParticipant}
            right={
              <TrainingParticipantMoreMenuButton
                onDeleteConfirm={() => onDeleteConfirm({ close })}
              />
            }
          >
            {renderHeadingLeft()}
          </SlideoverDialogHeader>

          <SlideoverDialogContent>
            <AudioPlayerProvider>
              <div className="grid h-full grid-cols-[minmax(0,1fr)_minmax(0,1fr)] grid-rows-[minmax(0,1fr)_auto]">
                <div className="relative overflow-y-auto px-6 py-4">
                  {!participant && isLoadingParticipant ? (
                    <DefaultSkeleton />
                  ) : null}

                  {!participant && isErrorParticipant ? (
                    <div className="text-red-500">
                      An error occurred while loading participant details.
                      Please refresh to try again.
                    </div>
                  ) : null}

                  {participant ? (
                    <div className="space-y-4">
                      <Heading
                        id={treeLabelId}
                        level={3}
                        className="text-base leading-7 font-semibold text-foreground"
                      >
                        Learning journey progress
                      </Heading>
                      <ParticipantCourseTree
                        treeLabelId={treeLabelId}
                        sectionsById={sectionsById}
                        participant={participant}
                        selectedKeys={selectedKeys}
                        onSelectionChange={onSelectionChange}
                      />
                    </div>
                  ) : null}
                </div>

                <div className="relative space-y-4 overflow-y-auto px-6 py-4">
                  {selectedSection ? (
                    <LessonDetail
                      key={selectedSection.id}
                      projectId={props.project.id}
                      participantId={participantId}
                      participantName={
                        buildPersonName(participant?.contact) ?? "Participant"
                      }
                      selectedLessonNumber={selectedLessonNumber}
                      selectedSection={selectedSection}
                    />
                  ) : selectedChapter ? (
                    <ChapterDetail
                      key={selectedChapter.id}
                      projectId={props.project.id}
                      participantId={participantId}
                      chapterId={selectedChapter.id}
                      chapterNumber={selectedChapterNumber}
                      participant={participant}
                    />
                  ) : (
                    <div className="text-sm">
                      Select a chapter to view grading details, or a lesson to
                      view the transcript.
                    </div>
                  )}
                </div>

                <div className="col-span-full">
                  <AudioPlayer />
                </div>
              </div>
            </AudioPlayerProvider>
          </SlideoverDialogContent>
        </>
      )}
    </SlideoverDialog>
  );
};
